import { useState, useEffect } from "react";
import {
  resolveApolloAccountId,
  type CompanySearchResult,
} from "@sales-ops-amplify/shared";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useAccount,
  useDirectoryCompany,
  useResearchAccount,
} from "@/hooks/useCompanies";
import {
  deleteAccount,
  enrichAccount,
  getAccountHealth,
} from "@/api/accounts";
import { saveCompanies } from "@/api/companies";
import { AccountEditForm } from "@/components/companies/account-edit-form";
import {
  CompanyHero,
  isCompanyTabId,
  type CompanyTabId,
} from "@/components/companies/company-hero";
import { CompanyDetailsRail } from "@/components/companies/company-details-rail";
import { CompanyTabs } from "@/components/companies/company-tabs";
import { CompanyOverviewTab } from "@/components/companies/tabs/company-overview-tab";
import { CompanyActivitiesTab } from "@/components/companies/tabs/company-activities-tab";
import { CompanyPeopleTab } from "@/components/companies/tabs/company-people-tab";
import { CompanyOpportunitiesTab } from "@/components/companies/tabs/company-opportunities-tab";
import { CompanyEnrichmentTab } from "@/components/companies/tabs/company-enrichment-tab";
import { CompanyFieldsTab } from "@/components/companies/tabs/company-fields-tab";
import {
  CompanyConversationsTab,
  CompanySequencesTab,
} from "@/components/companies/tabs/company-stub-tabs";
import { coalesceCompanyProfile } from "@/lib/company-profile";
import { useCompanyDetailRoute } from "@/lib/company-detail-route";

export function CompanyDetailPage() {
  const { detailId, isMutable, companiesBase, tab: tabParam } =
    useCompanyDetailRoute();
  const navigate = useNavigate();
  const activeTab: CompanyTabId = isCompanyTabId(tabParam) ? tabParam : "overview";
  const [editing, setEditing] = useState(false);
  const qc = useQueryClient();
  const accountQuery = useAccount(isMutable ? detailId : undefined);
  const directoryQuery = useDirectoryCompany(!isMutable ? detailId : undefined);
  const { data: merged, isLoading, error } = isMutable
    ? accountQuery
    : directoryQuery;
  const researchCompany = useResearchAccount();
  const { data: health } = useQuery({
    queryKey: ["companies", "health", detailId],
    queryFn: () => getAccountHealth(detailId!),
    enabled: !!detailId && isMutable,
    staleTime: 30_000,
  });
  const enrichCompany = useMutation({
    mutationFn: () => enrichAccount(detailId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
  const saveToMine = useMutation({
    mutationFn: (company: CompanySearchResult) =>
      saveCompanies({ companies: [company] }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      const dest = res.accounts[0]?.accountId;
      if (dest) navigate(`/user/accounts/${dest}`);
    },
  });
  const deleteCompany = useMutation({
    mutationFn: (id: string) => deleteAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      navigate("/user/accounts");
    },
  });

  const company = merged;
  const directory = company?.directory;
  const user = company?.user;
  const accountId = isMutable
    ? (company?.accountId ?? user?.accountId ?? detailId)
    : (user?.accountId ?? company?.accountId);
  const profile = company
    ? coalesceCompanyProfile(company, { preferDirectory: !isMutable })
    : null;
  const industry = profile?.industry;
  const domain = profile?.domain;
  const website = profile?.website;
  const linkedin = profile?.linkedinUrl;
  const locationLabel = profile?.address;
  const openOpportunityCount =
    health?.openOpportunityCount ??
    (company?.opportunities ?? []).filter(
      (o) => !["Won", "Lost", "Passed"].includes(o.stage ?? ""),
    ).length;
  const leadCoverageCount =
    health?.leadCoverageCount ?? company?.people?.length ?? 0;
  const researchFreshnessDays =
    health?.researchFreshnessDays ??
    (() => {
      const at = user?.lastResearchedAt;
      if (!at) return null;
      const t = new Date(at).getTime();
      if (Number.isNaN(t)) return null;
      return Math.floor((Date.now() - t) / 86_400_000);
    })();

  const canonicalAccountId = user?.accountId ?? company?.accountId;

  useEffect(() => {
    if (isLoading || !isMutable || !canonicalAccountId || !detailId) return;
    if (canonicalAccountId === detailId) return;
    const suffix =
      activeTab === "overview" ? "" : `/${activeTab}`;
    navigate(`/user/accounts/${canonicalAccountId}${suffix}`, { replace: true });
  }, [
    isLoading,
    isMutable,
    canonicalAccountId,
    detailId,
    activeTab,
    navigate,
  ]);

  function setTab(next: CompanyTabId) {
    const id = isMutable ? accountId : detailId;
    if (!id) return;
    const base = `${companiesBase}/${id}`;
    navigate(next === "overview" ? base : `${base}/${next}`);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error || !company || !detailId || (isMutable && (!accountId || !user))) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <Link
          to={companiesBase}
          className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline"
        >
          {isMutable ? "Back to Accounts" : "Back to Find companies"}
        </Link>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">Company not found.</p>
        </div>
      </div>
    );
  }

  const crmEditor =
    isMutable && user && accountId ? (
      <AccountEditForm account={user} accountId={accountId} />
    ) : null;

  return (
    <div className="mx-auto w-full min-w-0 max-w-7xl space-y-5 overflow-x-hidden">
      <CompanyHero
        merged={company}
        isMutable={isMutable}
        companiesBase={companiesBase}
        industry={industry}
        locationLabel={locationLabel}
        website={website}
        linkedinUrl={linkedin}
        onFindPeople={() =>
          navigate(
            `/people?company=${encodeURIComponent(company.name)}&mode=ai`,
          )
        }
        onSave={
          !isMutable && !user
            ? () => {
                const companyId =
                  directory?.id ??
                  resolveApolloAccountId(directory?.crm) ??
                  accountId ??
                  "";
                if (!companyId) return;
                saveToMine.mutate({
                  companyId,
                  apolloOrgId:
                    resolveApolloAccountId(directory?.crm) ?? undefined,
                  name: company.name,
                  accountId: company.accountId,
                  domain: domain ?? undefined,
                  industry: industry ?? undefined,
                  employeeCount: directory?.employeeCount,
                  city: directory?.hqCity,
                  state: directory?.hqRegion,
                  country: directory?.hqCountry,
                  websiteUrl: website,
                  logoUrl: directory?.logoUrl,
                  phone: directory?.phone,
                  description: directory?.description,
                  technologies: directory?.technologies,
                  keywords: directory?.keywords,
                });
              }
            : undefined
        }
        onEnrich={isMutable ? () => enrichCompany.mutate() : undefined}
        onResearch={
          isMutable && accountId
            ? () => researchCompany.mutate(accountId)
            : undefined
        }
        onDelete={
          isMutable && user && accountId
            ? () => {
                if (
                  !window.confirm(
                    `Delete ${company.name}? This permanently removes your copy.`,
                  )
                ) {
                  return;
                }
                deleteCompany.mutate(accountId);
              }
            : undefined
        }
        saving={saveToMine.isPending}
        enriching={enrichCompany.isPending}
        researching={researchCompany.isPending}
        deleting={deleteCompany.isPending}
      />

      <div className="grid min-w-0 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="min-w-0 lg:sticky lg:top-4 lg:self-start">
          <CompanyDetailsRail
            merged={company}
            description={profile?.description}
            industry={industry}
            keywords={profile?.keywords}
            employeeCount={profile?.employeeCount}
            foundedYear={profile?.foundedYear}
            phone={profile?.phone}
            domain={domain}
            website={website}
            linkedinUrl={linkedin}
            locationLabel={locationLabel}
          />
        </div>

        <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden">
          {isMutable && user && accountId ? (
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Account profile</h3>
                <button
                  type="button"
                  onClick={() => setEditing((v) => !v)}
                  className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted/40"
                >
                  {editing ? "Close editor" : "Edit"}
                </button>
              </div>
              {editing ? (
                <AccountEditForm
                  account={user}
                  accountId={accountId}
                  onCancel={() => setEditing(false)}
                  onSaved={() => {
                    setEditing(false);
                    void qc.invalidateQueries({ queryKey: ["companies"] });
                  }}
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Edit CRM fields, firmographics, notes, and external links for
                  this account.
                </p>
              )}
            </div>
          ) : null}

          <CompanyTabs active={activeTab} onChange={setTab} />
          <div role="tabpanel" className="min-h-[320px]">
            {activeTab === "overview" ? (
              <CompanyOverviewTab
                merged={company}
                isMutable={isMutable}
                openOpportunityCount={openOpportunityCount}
                leadCoverageCount={leadCoverageCount}
                researchFreshnessDays={researchFreshnessDays}
              />
            ) : null}
            {activeTab === "activities" ? (
              <CompanyActivitiesTab
                accountId={accountId ?? detailId}
                isMutable={isMutable}
              />
            ) : null}
            {activeTab === "people" ? (
              <CompanyPeopleTab
                merged={company}
                accountId={accountId ?? detailId}
                isMutable={isMutable}
              />
            ) : null}
            {activeTab === "opportunities" ? (
              <CompanyOpportunitiesTab merged={company} />
            ) : null}
            {activeTab === "sequences" ? <CompanySequencesTab /> : null}
            {activeTab === "conversations" ? (
              <CompanyConversationsTab />
            ) : null}
            {activeTab === "enrichment" ? (
              <CompanyEnrichmentTab
                merged={company}
                isMutable={isMutable}
                onEnrich={() => enrichCompany.mutate()}
                onResearch={() =>
                  accountId ? researchCompany.mutate(accountId) : undefined
                }
                enriching={enrichCompany.isPending}
                researching={researchCompany.isPending}
                enrichMessage={
                  enrichCompany.isSuccess
                    ? (enrichCompany.data?.message ?? "Enriched")
                    : undefined
                }
                enrichError={
                  enrichCompany.isError
                    ? enrichCompany.error instanceof Error
                      ? enrichCompany.error.message
                      : "Enrich failed"
                    : undefined
                }
              />
            ) : null}
            {activeTab === "fields" ? (
              <CompanyFieldsTab merged={company} crmEditor={crmEditor} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
